import React, { useEffect, useState } from "react";

import {
  Subscribe,
  SubscribeSuccessPayload,
} from "@modules/Subscriptions/Flows/Subscribe/Subscribe";
import { useSearchParams } from "@Libs/Router/hooks/useSearchParams";
import { useLocation } from "@Libs/Router/hooks/useLocation";
import { useMetadata } from "@Libs/Events/hooks/useMetadata";
import { LoadingScreen } from "@UI/Components/LoadingScreen/LoadingScreen";
import { useNavigate } from "@Libs/Router/hooks/useNavigate";
import { useCurrentUser } from "@modules/Authentication/hooks/useCurrentUser";
import { ConfirmationModal } from "@modules/Subscriptions/Components/ConfirmationModal/ConfirmationModal";

const CHECKOUT_COMPLETED_KEY = "subscriptionCheckoutCompleted";

const readCheckoutCompleted = () => {
  try {
    return sessionStorage.getItem(CHECKOUT_COMPLETED_KEY) === "1";
  } catch {
    return false;
  }
};

const writeCheckoutCompleted = (completed: boolean) => {
  try {
    if (completed) {
      sessionStorage.setItem(CHECKOUT_COMPLETED_KEY, "1");
    } else {
      sessionStorage.removeItem(CHECKOUT_COMPLETED_KEY);
    }
  } catch {
    // storage unavailable: the back-navigation redirect is best effort
  }
};

const isBackForwardNavigation = () => {
  const [entry] = performance.getEntriesByType(
    "navigation"
  ) as PerformanceNavigationTiming[];
  return entry?.type === "back_forward";
};

export const GenericSubscriptionFlow = () => {
  const { queryParams } = useLocation();
  const navigate = useNavigate();
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [subscribeSuccessPayload, setSubscribeSuccessPayload] =
    useState<SubscribeSuccessPayload>(null);
  const returnUrl = useSearchParams("returnUrl");
  const context = useSearchParams("context");
  const { currentUser, isLoading } = useCurrentUser({
    includes: ["currentSubscription"],
  });

  const hasActiveSubscription = !!currentUser?.currentSubscription;
  const [hasCompletedCheckout, setHasCompletedCheckout] = useState(
    () => readCheckoutCompleted() && isBackForwardNavigation()
  );

  const { setMetadata } = useMetadata();

  useEffect(() => {
    setMetadata({ context: context || "subscriptionFlow" });
  }, [context]);

  // Already subscribed (e.g. browser back after paying): replace the checkout history entry.
  useEffect(() => {
    if (hasActiveSubscription) {
      navigate("MY_SUBSCRIPTION", { reload: true, replace: true });
    }
  }, [hasActiveSubscription]);

  // Checkout already completed (e.g. browser back after paying, even by bank transfer
  // where the subscription is not active yet): go back to the start.
  useEffect(() => {
    const redirectHomeIfCheckoutCompleted = (isBackNavigation: boolean) => {
      if (!readCheckoutCompleted()) return;
      writeCheckoutCompleted(false);
      // a fresh visit (e.g. subscribe CTA clicked again) must not be redirected
      if (isBackNavigation) {
        setHasCompletedCheckout(true);
        navigate("CLIENT_DASHBOARD", { reload: true, replace: true });
      }
    };
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) redirectHomeIfCheckoutCompleted(true);
    };

    redirectHomeIfCheckoutCompleted(isBackForwardNavigation());
    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  const handleLeave = async () => {
    if (returnUrl) {
      window.location.replace(returnUrl);
    } else {
      navigate("ROOT", { reload: true, replace: true });
    }
  };

  const handleSuccess = (payload: SubscribeSuccessPayload) => {
    writeCheckoutCompleted(true);
    setIsSuccessModalOpen(true);
    setSubscribeSuccessPayload(payload);
  };

  const isContextSet = context !== undefined;

  if (!isContextSet || isLoading) {
    return <LoadingScreen />;
  }

  if (hasActiveSubscription || hasCompletedCheckout) {
    return <LoadingScreen />;
  }

  return (
    <>
      <Subscribe
        onLeave={handleLeave}
        rootRoute={{ to: ["SUBSCRIBE"], queryParams }}
        backRoute={{ to: ["ROOT"], queryParams }}
        onSuccess={handleSuccess}
      />
      <ConfirmationModal
        isOpen={isSuccessModalOpen}
        onConfirm={handleLeave}
        paymentMethod={subscribeSuccessPayload?.paymentMethod}
        isPaymentConfirmed={subscribeSuccessPayload?.isPaymentConfirmed}
      />
    </>
  );
};
